import React, { Dispatch, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { vars } from '../../../../../Theme';
import QualityGate from '../_parts/QualityGate';
import { DialogContent } from '@mui/material';
import DialogHeader from '../../_parts/DialogHeader';
import InitialReleaseContent from '../_parts/InitialReleaseContent';
import { includes, pickBy } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../../../redux/store';
import { updateSelectedDocuments } from '../../../../../redux/project/documents/sliceProjectSelectedDocuments';
import ReleaseFooterActions from '../_parts/ReleaseFooterActions';
import DialogFrame from '../../_parts/DialogFrame';
import { useParams } from 'react-router-dom';
import {
    fetchImportDocTypes,
    fetchImportViewData,
    fetchImportViews,
} from '../../../../../util/ImportUtils';
import {
    getDataForMainDialog,
    getNewIndexInitialOption,
} from '../../../../../redux/project/sliceProjectWorkflow';
import { uploadVdcViewerDocument } from '../../../../../redux/project/documents/sliceProjectVdcCollaboration';
import { findProjectDocument } from '../../../../../util/DocUtils';

export default ({ open, onClose }: IWorkflowInitialReleaseDialogProps) => {
    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'initialReleaseDialog' });
    const dispatch: Dispatch<unknown> = useDispatch();
    const { replicaId, areaName } = useParams();

    const [isQualityGateStep, setIsQualityGateStep] = useState(false);
    const [mainState, setMainState] = React.useState<InitialRelaseState>({});
    const [hasError, setHasError] = React.useState(false);

    const completedWorkflowInitialRelease = useSelector(
        (_store: IAppReduxStore) => _store.projectWorkflow.completedWorkflowInitialRelease,
    );
    const docUnIdsForForwardDialog = useSelector(
        (_store: IAppReduxStore) => _store.projectWorkflow.docUnIdsForForwardDialog,
    );
    const invalidInitialReleaseDocs = useSelector(
        (_store: IAppReduxStore) => _store.projectWorkflow.initialReleaseData?.invalidDocuments,
    );
    const newIndexInitialOption = useSelector(getNewIndexInitialOption);

    useEffect(
        () => setIsQualityGateStep(invalidInitialReleaseDocs?.documents?.length > 0),
        [invalidInitialReleaseDocs],
    );

    useEffect(() => {
        if (newIndexInitialOption) {
            setMainState(prev => ({
                ...prev,
                newRevision: newIndexInitialOption.workflowTask[0]?.path,
            }));
        }
    }, [newIndexInitialOption]);

    useEffect(() => {
        if (completedWorkflowInitialRelease) {
            dispatch(
                updateSelectedDocuments({
                    replicaId,
                    areaName,
                    documentIds: [],
                }),
            );
            fetchImportViews(replicaId);
            fetchImportViewData(replicaId);
            fetchImportDocTypes(replicaId);
            if (docUnIdsForForwardDialog?.length > 0) {
                dispatch(
                    getDataForMainDialog({ replicaId, documentIds: docUnIdsForForwardDialog }),
                );
            }

            const partialModelPattern = /^#Document#_.*_PartialModel$/;

            const documentPartialModels = docUnIdsForForwardDialog
                .map(docUnId => findProjectDocument(docUnId, areaName))
                .filter(document => partialModelPattern.test(document.DocType));

            if (documentPartialModels.length > 0) {
                dispatch(
                    uploadVdcViewerDocument({
                        replicaId,
                        documentIds: documentPartialModels.map(doc => doc.UniversalID),
                    }),
                );
            }
            closeDialog();
        }
    }, [completedWorkflowInitialRelease, docUnIdsForForwardDialog]);

    const handleCancel = () => {
        // Close the dialog without performing any action
        closeDialog();
    };

    const closeDialog = () => {
        setMainState({});
        setHasError(false);
        setIsQualityGateStep(false);
        onClose();
    };

    const getStateChanged = (arg: StateChangedArgumentsInitialRelease) => {
        setHasError(false);

        setMainState(prev => {
            if (arg.parentValue.includes('<|>options')) {
                return { ...prev, [arg.parentValue]: arg.state };
            }
            const filteredPrev = pickBy(prev, (value, key) => !includes(key, arg.parentValue));
            return { ...filteredPrev, [arg.parentValue]: arg.state };
        });
    };

    return (
        <DialogFrame open={open}>
            <DialogHeader
                onClose={handleCancel}
                dialogTitle={
                    isQualityGateStep && invalidInitialReleaseDocs
                        ? t('qualityCheck')
                        : t('documentRelease')
                }
                chipColor={vars.chipDarkBlue}
                chipLabel={t('chipLabel')}
            />

            <DialogContent sx={{ pt: 0, overflowX: 'hidden' }}>
                {isQualityGateStep && invalidInitialReleaseDocs ? (
                    <QualityGate />
                ) : (
                    <InitialReleaseContent
                        getStateChanged={getStateChanged}
                        hasError={hasError}
                        mainState={mainState}
                    />
                )}
            </DialogContent>
            <ReleaseFooterActions
                isQualityGateStep={isQualityGateStep}
                setIsQualityGateStep={setIsQualityGateStep}
                mainState={mainState}
                setHasError={setHasError}
                getStateChanged={getStateChanged}
            />
        </DialogFrame>
    );
};

/**
 * Interface that describes WorkflowMainDialog's props.
 */
interface IWorkflowInitialReleaseDialogProps extends React.PropsWithChildren {
    open: boolean;
    onClose?: () => void;
}

export interface StateChangedArgumentsInitialRelease {
    parentValue: string;
    state: string;
}

export interface InitialRelaseState {
    [key: string]: string;
}
